import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Archive, Check, CheckCircle2, Pencil, Plus, Search, SlidersHorizontal, X } from 'lucide-react'
import { api } from '../lib/apiClient'
import { entityConfigs } from '../lib/config'
import { classNames, formatMoney, formatPercent, isOverdue } from '../lib/formatters'
import type { AppRecord } from '../types'
import { useApi } from '../hooks/useApi'
import { DataTable } from '../components/DataTable'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import { Modal } from '../components/Modal'
import { PageHeader } from '../components/PageHeader'
import { RecordForm } from '../components/RecordForm'
import { LeadConversionButton } from '../components/LeadConversionButton'
import { columnsForOpportunityCurrentValue, summarizeOpportunityListRevenue, withCurrentOpportunityRevenue } from '../lib/opportunityRevenue'

export function EntityListPage({ entity }: { entity: string }) {
  const config = entityConfigs[entity]
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState(entity === 'tasks' ? 'open' : '')
  const [secondaryFilter, setSecondaryFilter] = useState('')
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [showLost, setShowLost] = useState(false)
  const [editing, setEditing] = useState<AppRecord | null>(null)
  const [creating, setCreating] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const { data, loading, error, reload } = useApi(
    () => api.list<AppRecord>(entity, showArchived ? '?includeArchived=true' : ''),
    [entity, showArchived],
  )

  const records = useMemo(() => {
    const term = search.trim().toLowerCase()
    const filtered = (data ?? []).filter((record) => {
      if (entity === 'opportunities' && !showLost && String(record.status ?? '').toLowerCase() === 'lost') return false
      const matchesSearch = !term || config.searchKeys.some((key) => {
        const value = record[key]
        const searchable = Array.isArray(value) ? value.map((item) => typeof item === 'object' && item && 'name' in item ? item.name : item).join(' ') : value
        return String(searchable ?? '').toLowerCase().includes(term)
      })
      const matchesFilter = !filter || String(record[config.filterKey ?? '']) === filter
      const matchesSecondary = !secondaryFilter || String(record[config.secondaryFilterKey ?? '']) === secondaryFilter
      const matchesOverdue = !overdueOnly || (record.status !== 'done' && isOverdue(record.dueDate))
      return matchesSearch && matchesFilter && matchesSecondary && matchesOverdue
    })
    return filtered.sort((a, b) => {
      if (entity === 'tasks') {
        const aDate = a.dueDate ? new Date(String(a.dueDate)).getTime() : Number.MAX_SAFE_INTEGER
        const bDate = b.dueDate ? new Date(String(b.dueDate)).getTime() : Number.MAX_SAFE_INTEGER
        return aDate - bDate
      }
      return new Date(String(b.createdTime ?? 0)).getTime() - new Date(String(a.createdTime ?? 0)).getTime()
    })
  }, [config, data, entity, filter, overdueOnly, search, secondaryFilter, showLost])

  const opportunitySummary = useMemo(() => entity === 'opportunities' ? summarizeOpportunityListRevenue(records) : null, [entity, records])

  const flash = (message: string) => {
    setNotice(message)
    window.setTimeout(() => setNotice(null), 3000)
  }
  const save = async (values: Record<string, unknown>) => {
    if (entity === 'leads' && !values.phone && !values.email) throw new Error('Add at least a phone number or email address.')
    const savedRecord = editing
      ? await api.update<AppRecord>(entity, editing.id, values)
      : await api.create<AppRecord>(entity, values)
    setCreating(false)
    setEditing(null)
    await reload()
    flash(savedRecord.folderProvisioningError
      ? 'Opportunity saved, but its Drive folder could not be created. Open it to retry.'
      : `${config.singular.charAt(0).toUpperCase() + config.singular.slice(1)} saved.`)
  }
  const patch = async (record: AppRecord, values: Record<string, unknown>, message: string) => {
    await api.update(entity, record.id, values)
    await reload()
    flash(message)
  }
  const archive = async (record: AppRecord) => {
    if (!window.confirm(`Archive this ${config.singular}? It will be hidden from the default view.`)) return
    if (!config.statusKey) return
    await patch(record, { [config.statusKey]: config.archiveValue }, `${config.singular.charAt(0).toUpperCase() + config.singular.slice(1)} archived.`)
  }
  const openRecord = (record: AppRecord) => {
    if (config.detail) navigate(`/${entity}/${record.id}`)
    else setEditing(record)
  }

  const hasFilters = Boolean(search || filter || secondaryFilter || overdueOnly)
  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title={config.title}
        description={config.description}
        actions={<button className="btn-primary" onClick={() => setCreating(true)}><Plus className="h-4 w-4" />{config.createLabel}</button>}
      />

      {notice && <div className="fixed right-5 top-20 z-[60] flex items-center gap-2 rounded-lg bg-pine-900 px-4 py-3 text-sm font-semibold text-white shadow-xl"><Check className="h-4 w-4 text-pine-300" />{notice}<button onClick={() => setNotice(null)} className="ml-2 text-white/60 hover:text-white"><X className="h-4 w-4" /></button></div>}

      <section className="card overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input className="field-input pl-9" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={config.searchPlaceholder} aria-label="Search" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {config.filterOptions && (
              <select className="field-input w-auto min-w-[145px] py-2.5" value={filter} onChange={(event) => setFilter(event.target.value)}>
                <option value="">{config.filterLabel}</option>
                {config.filterOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            )}
            {config.secondaryFilterOptions && (
              <select className="field-input w-auto min-w-[145px] py-2.5" value={secondaryFilter} onChange={(event) => setSecondaryFilter(event.target.value)}>
                <option value="">{config.secondaryFilterLabel}</option>
                {config.secondaryFilterOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            )}
            {entity === 'tasks' && (
              <button className={classNames('btn-secondary px-3', overdueOnly && 'border-rose-200 bg-rose-50 text-rose-700')} onClick={() => setOverdueOnly((value) => !value)}>
                <SlidersHorizontal className="h-4 w-4" /> Overdue
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-white px-5 py-3 text-xs text-slate-500">
          <span><strong className="font-semibold text-slate-700">{records.length}</strong> {records.length === 1 ? config.singular : config.plural}</span>
          <div className="flex flex-wrap items-center gap-4">
            {entity === 'opportunities' && <label className="flex cursor-pointer items-center gap-2"><input type="checkbox" className="h-3.5 w-3.5 rounded border-slate-300 text-pine-700 focus:ring-pine-500" checked={showLost} onChange={(event) => setShowLost(event.target.checked)} /> Show lost</label>}
            {typeof config.archiveValue === 'string' && (
              <label className="flex cursor-pointer items-center gap-2"><input type="checkbox" className="h-3.5 w-3.5 rounded border-slate-300 text-pine-700 focus:ring-pine-500" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} /> Show archived</label>
            )}
          </div>
        </div>

        {opportunitySummary && !loading && !error && <section aria-label="Opportunity financial summary" className="border-b border-slate-100 bg-slate-50/60 px-5 py-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div><h2 className="text-xs font-semibold text-slate-500">Revenue net</h2><p className="mt-1 text-xl font-bold tabular-nums text-slate-950">{formatMoney(opportunitySummary.revenue)}</p><p className="mt-1 text-xs text-slate-500">Sum · VAT exclusive</p></div>
            <div><h2 className="text-xs font-semibold text-slate-500">Direct-cost profit</h2><p className="mt-1 text-xl font-bold tabular-nums text-slate-950">{formatMoney(opportunitySummary.profit)}</p><p className="mt-1 text-xs text-slate-500">{opportunitySummary.missingProfitCount ? 'Unavailable: some contributing Opportunities have missing profit' : 'Sum · after direct costs'}</p></div>
            <div><h2 className="text-xs font-semibold text-slate-500">Margin</h2><p className="mt-1 text-xl font-bold tabular-nums text-slate-950">{formatPercent(opportunitySummary.marginPct)}</p><p className="mt-1 text-xs text-slate-500">Average · {opportunitySummary.marginSampleCount} non-zero {opportunitySummary.marginSampleCount === 1 ? 'margin' : 'margins'}</p></div>
          </div>
          <p className="mt-3 text-xs text-slate-500">Current filtered Opportunities · excludes Lost and Archived · Accepted → Active offer → Latest quoted</p>
        </section>}

        {loading ? <LoadingState /> : error ? <ErrorState message={error} onRetry={reload} /> : records.length ? (
          <DataTable
            records={entity === 'opportunities' ? records.map(withCurrentOpportunityRevenue) : records}
            columns={entity === 'opportunities' ? columnsForOpportunityCurrentValue(config.columns) : config.columns}
            minWidth={entity === 'opportunities' ? 1300 : undefined}
            onRowClick={openRecord}
            renderActions={(record) => (
              <div className="flex items-center justify-end gap-1">
                {entity === 'tasks' && record.status !== 'done' && <button className="rounded-md p-2 text-pine-600 hover:bg-pine-50" title="Mark done" onClick={() => void patch(record, { status: 'done', completedAt: new Date().toISOString() }, 'Task completed.')}><CheckCircle2 className="h-4 w-4" /></button>}
                {entity === 'leads' && !['converted', 'lost', 'archived'].includes(String(record.status)) && <LeadConversionButton lead={record} compact />}
                <button className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Edit" disabled={entity === 'quotes' && String(record.status || '').toLowerCase() === 'superseded'} onClick={() => setEditing(record)}><Pencil className="h-4 w-4" /></button>
                {config.statusKey && record[config.statusKey] !== config.archiveValue && entity !== 'quotes' && <button className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-rose-600" title="Archive" onClick={() => void archive(record)}><Archive className="h-4 w-4" /></button>}
              </div>
            )}
          />
        ) : (
          <EmptyState
            title={hasFilters ? `No ${config.plural} match your filters` : `No ${config.plural} yet`}
            description={hasFilters ? 'Try changing or clearing your search and filters.' : `Create your first ${config.singular} to get started.`}
            action={hasFilters ? <button className="btn-secondary" onClick={() => { setSearch(''); setFilter(''); setSecondaryFilter(''); setOverdueOnly(false) }}>Clear filters</button> : <button className="btn-primary" onClick={() => setCreating(true)}><Plus className="h-4 w-4" />{config.createLabel}</button>}
          />
        )}
      </section>

      <Modal open={creating || Boolean(editing)} onClose={() => { setCreating(false); setEditing(null) }} title={editing ? `Edit ${config.singular}` : config.createLabel} description={editing ? `Update the details for this ${config.singular}.` : `Add the key information now; you can update it at any time.`} size="xl">
        <RecordForm fields={config.fields} initialValues={editing} submitLabel={editing ? 'Save changes' : config.createLabel} onSubmit={save} onCancel={() => { setCreating(false); setEditing(null) }} />
      </Modal>
    </>
  )
}
